import { useMemo } from "react";
import { buildSkyScene } from "../utils/sky";

type SkyBackgroundProps = {
  skyDate: Date;
  centerAzimuth: number;
};

const starRadius = (mag: number) => Math.max(0.14, 0.82 - mag * 0.12);

export function SkyBackground({ skyDate, centerAzimuth }: SkyBackgroundProps) {
  const scene = useMemo(() => buildSkyScene(skyDate, centerAzimuth), [centerAzimuth, skyDate]);
  const twilight = Math.max(0, Math.min(1, (scene.sunAltitude + 18) / 18));

  return (
    <div className="sky-background" aria-hidden="true">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none">
        <defs>
          <radialGradient id="skyGlow" cx="52%" cy="76%" r="74%">
            <stop offset="0%" stopColor={`rgba(255, 181, 90, ${0.18 + twilight * 0.22})`} />
            <stop offset="42%" stopColor="rgba(42, 50, 86, 0.54)" />
            <stop offset="100%" stopColor="rgba(4, 8, 22, 0.96)" />
          </radialGradient>
          <filter id="starBlur">
            <feGaussianBlur stdDeviation="0.13" />
          </filter>
        </defs>

        <rect width="100" height="100" fill="url(#skyGlow)" />
        <path className="sky-grid" d="M0 88 C24 77 77 77 100 88" />
        <path className="sky-grid faint" d="M4 63 C28 55 74 55 96 63" />
        <path className="sky-grid faint" d="M9 39 C33 32 69 32 91 39" />
        <path className="sky-meridian" d="M50 8 C49 30 49 60 50 93" />

        {scene.constellations.map((line) => (
          <polyline
            key={line.id}
            className="constellation-line"
            points={line.points.map((point) => `${point.x},${point.y}`).join(" ")}
          />
        ))}

        {scene.stars.map((star) => (
          <g key={star.id} className="sky-star">
            <circle cx={star.x} cy={star.y} r={starRadius(star.mag)} filter="url(#starBlur)" />
            {star.mag < 1.4 && (
              <text x={star.x + 1.2} y={star.y - 1.1}>
                {star.name}
              </text>
            )}
          </g>
        ))}

        {scene.bodies.map((body) => (
          <g key={body.id} className={`sky-body sky-body-${body.kind}`}>
            <circle cx={body.x} cy={body.y} r={body.kind === "moon" ? 1.25 : 0.72} />
            <text x={body.x + 1.6} y={body.y - 1.4}>
              {body.name}
            </text>
          </g>
        ))}

        <path className="sky-horizon" d="M0 91 C25 83 45 88 61 84 C77 80 91 83 100 76 L100 100 L0 100 Z" />
        {scene.cardinals.map((cardinal) => (
          <text key={cardinal.id} className="cardinal" x={cardinal.x} y={cardinal.y} textAnchor="middle">
            {cardinal.label}
          </text>
        ))}
      </svg>
    </div>
  );
}
